import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Modal } from "../../components/Modal/Modal";
import { Button } from "../../components/Button/Button";
import { useToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";

import { AdminUser, enableUser } from "./api";

interface Props {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
}

/**
 * Re-enable confirmation modal — the simplest of the action modals; the
 * mockup notes mention this is a plain confirm. Restores sign-in access
 * and the previous role + project memberships.
 */
export function ReEnableModal({ open, user, onClose }: Props) {
  const qc = useQueryClient();
  const { push } = useToast();

  const mut = useMutation({
    mutationFn: (id: string) => enableUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      push({
        variant: "success",
        message: "User re-enabled.",
      });
      onClose();
    },
    onError: (err: unknown) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  if (!user) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Re-enable ${user.name}?`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => mut.mutate(user.id)}
            disabled={mut.isPending}
            loading={mut.isPending}
          >
            Re-enable account
          </Button>
        </>
      }
    >
      <p>
        {user.name} will be able to sign in again. Their previous role and
        project memberships are restored.
      </p>
    </Modal>
  );
}
